import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal, For } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Radio from '~/components/Radio'

const RadioPage: Component = () => {
  const [selectedRole, setSelectedRole] = createSignal('member')

  const roles = [
    { id: 'admin', label: 'Administrador Full', desc: 'Acceso total a la infraestructura y finanzas.' },
    { id: 'editor', label: 'Editor de Contenido', desc: 'Puede crear y modificar contenido del sitio.' },
    { id: 'member', label: 'Miembro Estándar', desc: 'Acceso básico de lectura y participación.' }
  ]

  return (
    <>
      <Title>Radio - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Radio</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Sistema de selección mutuamente excluyente. El componente Radio proporciona una interfaz clara para tomar decisiones únicas dentro de un conjunto de opciones, con soporte para etiquetas
            informativas y estados de validación.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes de Color y Tamaño */}
          <Card>
            <Card.Header
              title="Paleta de Marca y Escala"
              subtitle="Alinea la selección visual con la jerarquía de tu diseño mediante colores y escalas."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-2xl border border-border">
                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Colores Semánticos</h4>
                  <div class="grid grid-cols-2 gap-4">
                    <Radio label="Primary" color="primary" name="c1" checked />
                    <Radio label="Secondary" color="secondary" name="c1" />
                    <Radio label="Success" color="success" name="c1" />
                    <Radio label="Danger" color="danger" name="c1" />
                  </div>
                </div>
                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-secondary-500">Dimensiones</h4>
                  <div class="flex flex-col gap-4 justify-center h-full">
                    <Radio label="Pequeño (sm)" size="sm" name="s1" />
                    <Radio label="Estándar (md)" size="md" name="s1" checked />
                    <Radio label="Grande (lg)" size="lg" name="s1" />
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Caso de Uso: Roles de Usuario */}
          <Card>
            <Card.Header
              title="Selección de Contexto"
              subtitle="Implementación de radio buttons con descripciones enriquecidas para mayor claridad."
              icon={<Icon icon="material-symbols-light:record-voice-over-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-2xl mx-auto space-y-4">
                <For each={roles}>
                  {role => (
                    <div
                      class={`p-6 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-4 ${selectedRole() === role.id ? 'border-primary-500 bg-primary-soft' : 'border-border bg-surface'}`}
                      onClick={() => setSelectedRole(role.id)}
                    >
                      <Radio name="roles" value={role.id} checked={selectedRole() === role.id} onChange={() => setSelectedRole(role.id)} class="mt-1" />
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <span class="font-bold text-foreground uppercase text-xs tracking-wider">{role.label}</span>
                          {role.id === 'admin' && (
                            <Badge color="danger" size="sm">
                              Restringido
                            </Badge>
                          )}
                        </div>
                        <p class="text-sm text-foreground-subtle italic">{role.desc}</p>
                      </div>
                    </div>
                  )}
                </For>
                <div class="mt-6 flex justify-end">
                  <div class="text-[10px] font-mono p-2 bg-slate-900 text-emerald-400 rounded-lg">
                    CURRENT_STATE_ID: <span class="font-bold uppercase tracking-widest">{selectedRole()}</span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Radio."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">checked</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Estado de selección del control.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">color</td>
                      <td class="px-6 py-4 italic text-xs">MainColorsType</td>
                      <td class="px-6 py-4 font-mono">'primary'</td>
                      <td class="px-6 py-4 italic">Color temático del anillo de selección.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic text-xs">'sm' | 'md' | 'lg'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Escala física del círculo.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold italic">helperText</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic text-balance">Texto informativo de soporte debajo de la etiqueta.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default RadioPage
